import "./surfaces/tripMaster/tripMasterBoot";
import React from "react";
import ReactDOM from "react-dom/client";

import "./axiosSetup";

if (typeof window !== "undefined") {
  window.__PERF_ENABLED__ = false;
  if (!window.__HR_ORIGINAL_CONSOLE_CACHED__) {
    window.__HR_ORIGINAL_CONSOLE_CACHED__ = true;
    try {
      window.__HR_ORIGINAL_CONSOLE_INFO__ = console.info.bind(console);
      window.__HR_ORIGINAL_CONSOLE_LOG__ = console.log.bind(console);
      window.__HR_ORIGINAL_CONSOLE_DEBUG__ = console.debug.bind(console);
    } catch {
      /* ignore */
    }
  }
  if (!window.__HR_CONSOLE_SILENCED__) {
    const noop = () => {};
    console.log = noop;
    console.info = noop;
    console.debug = noop;
    window.__HR_CONSOLE_SILENCED__ = true;
  }
  
  // Clear old sessionStorage filters to fix search results issue
  try {
    const VERSION_KEY = 'hr_filter_fix_version';
    const CURRENT_VERSION = '1.0.0';
    const stored = sessionStorage.getItem(VERSION_KEY);
    
    if (stored !== CURRENT_VERSION) {
      sessionStorage.removeItem('vp_results_ui_v1');
      sessionStorage.removeItem('vp_return_snapshot_v1');
      sessionStorage.setItem(VERSION_KEY, CURRENT_VERSION);
    }
  } catch {
    /* ignore */
  }
}

// CSS
import "./index.css";
import App from "./App";
import { getGlobalPerfState, logPerfFe, markBundleLoad } from "./utils/perf/perfMonitor";

// Languages
import { I18nextProvider } from "react-i18next";
import i18next from "i18next";
import languageDetector from "i18next-browser-languagedetector";
import { AppProvider } from "./contexts/AppContext";
import { completepassengersHeFallback } from "./i18n/completepassengersHeFallback";
import { successorderHeFallback } from "./i18n/successorderHeFallback";

/** Stable URL for locale JSON (Vite public/ → site root; works on Amplify). */
function getLocaleJsonUrl(lang) {
  const base = String(import.meta.env.BASE_URL || "/").replace(/\/?$/, "/");
  return `${base}locales/${encodeURIComponent(lang)}.json`;
}

function mergeHeWithPassengerCheckoutFallback(bundle) {
  const b = bundle && typeof bundle === "object" ? bundle : {};
  const existing = b.completepassengers && typeof b.completepassengers === "object" ? b.completepassengers : {};
  const successExisting = b.successorder && typeof b.successorder === "object" ? b.successorder : {};
  return {
    ...b,
    completepassengers: {
      ...completepassengersHeFallback,
      ...existing,
    },
    successorder: {
      ...successorderHeFallback,
      ...successExisting,
    },
  };
}

async function fetchLocaleObject(lang) {
  try {
    const res = await fetch(getLocaleJsonUrl(lang), {
      credentials: "same-origin",
      cache: import.meta.env.DEV ? "no-store" : "default",
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data && typeof data === "object" ? data : null;
  } catch {
    return null;
  }
}

/**
 * Load translation for a language; on failure use Hebrew; never throw.
 */
async function loadTranslationSafe(lang) {
  const primary = await fetchLocaleObject(lang);
  if (primary && Object.keys(primary).length > 0) return primary;
  if (lang !== "he") {
    const heb = await fetchLocaleObject("he");
    if (heb && Object.keys(heb).length > 0) return heb;
  }
  return {};
}

async function initializeApp() {
  const perfState = getGlobalPerfState();
  logPerfFe("APP_BOOT", { session_id: perfState.sessionId });

  const heT = mergeHeWithPassengerCheckoutFallback(await loadTranslationSafe("he"));

  const resources = {
    he: { translation: heT },
  };

  const i18nDetection = {
    // Do not use navigator/htmlTag: browser locale was forcing English for new visitors.
    order: ["localStorage"],
    caches: ["localStorage"],
    lookupLocalStorage: "i18nextLng",
  };

  try {
    await i18next.use(languageDetector).init({
      interpolation: { escapeValue: false },
      fallbackLng: "he",
      supportedLngs: ["he"],
      nonExplicitSupportedLngs: true,
      lng: "he",
      resources,
      detection: i18nDetection,
      keySeparator: ".",
      nsSeparator: ":",
    });
  } catch (e) {
    console.warn("[i18n] init failed, using empty bundles", e);
    await i18next.use(languageDetector).init({
      interpolation: { escapeValue: false },
      fallbackLng: "he",
      supportedLngs: ["he"],
      lng: "he",
      resources: {
        he: { translation: mergeHeWithPassengerCheckoutFallback({}) },
      },
      detection: i18nDetection,
      keySeparator: ".",
      nsSeparator: ":",
    });
  }

  try {
    localStorage.setItem("i18nextLng", "he");
  } catch {
    /* ignore */
  }
  await i18next.changeLanguage("he");

  const root = ReactDOM.createRoot(document.getElementById("root"));

  root.render(
    <I18nextProvider i18n={i18next}>
      <AppProvider>
        <App />
      </AppProvider>
    </I18nextProvider>
  );

  if (typeof window !== "undefined") {
    const handleLoad = () => {
      markBundleLoad();
      window.removeEventListener("load", handleLoad);
    };
    if (document.readyState === "complete") {
      handleLoad();
    } else {
      window.addEventListener("load", handleLoad, { once: true });
    }
  }
}

initializeApp().catch((err) => {
  console.error("App failed to initialize:", err);
  const root = document.getElementById("root");
  if (root) {
    root.innerHTML = [
      "<div style='padding:20px;font-family:sans-serif;max-width:600px;'>",
      "<h2>שגיאת טעינה</h2>",
      "<pre style='background:#f5f5f5;padding:12px;overflow:auto;'>" +
        (err?.message || String(err)) +
        "</pre>",
      "<p>פתח Console (F12) לפרטים.</p>",
      "</div>",
    ].join("");
  }
});
